import { dehydrate, DehydratedState, QueryClient } from "@tanstack/react-query";
import type { GetServerSideProps, NextPage } from "next";
import Head from "next/head";
import React from "react";
import { ESeoPage } from "@/utils/mockData/metaMocks";
import useHomeData from "@/utils/queries/hooks/useHomeData";
import { getOpenGraphData } from "@/utils/seo";
import useNavBarData from "@/utils/queries/hooks/useNavBarData";
import useGeneralItalianStoriesData from "@/utils/queries/hooks/useGeneralItalianStoriesData";
import Seo from "@/components/common/Seo";
import { withRedirect } from "@/utils/redirects";
import HomeBody from "../components/home/HomeBody";
import {
    getBrochureData,
    getHomeData,
    getNavBarData,
    getSeoInfo,
    getVideoReviewsData,
} from "../utils/queries";

const Home: NextPage = () => {
    // const { data: metaTags } = useQuery({
    //     queryKey: ["metaTags", ESeoPage.HOME],
    //     queryFn: () => getSeoInfo(ESeoPage.HOME),
    // });
    const { data: homeData } = useHomeData();
    const { data: navBarData } = useNavBarData();
    const { data: italianStoriesData } = useGeneralItalianStoriesData();
    const metadata = getOpenGraphData(homeData, navBarData, italianStoriesData);
    const headImage = homeData?.staticMedia.headImage;

    return (
        <>
            <Seo
                seoInfo={metadata}
                noindex={!homeData?.staticMedia.metadata?.index}
            />
            {/* Preload dell'immagine LCP per migliorare le performance */}
            {headImage && (
                <Head>
                    <link
                        rel="preload"
                        as="image"
                        href={headImage}
                        {...({
                            fetchPriority: "high",
                        } as React.LinkHTMLAttributes<HTMLLinkElement>)}
                    />
                </Head>
            )}
            <HomeBody
                homeData={homeData}
                navBarData={navBarData}
                italianStoriesData={italianStoriesData}
            />
        </>
    );
};

export const getServerSideProps: GetServerSideProps = withRedirect(
    async (): Promise<{
        props: {
            dehydratedState: DehydratedState;
        };
    }> => {
        const queryClient = new QueryClient();
        await Promise.all([
            await queryClient.prefetchQuery(["navBar"], getNavBarData),
            await queryClient.prefetchQuery(["brochures"], getBrochureData),
            await queryClient.prefetchQuery(
                ["videoReview"],
                getVideoReviewsData
            ),
            await queryClient.prefetchQuery(["home"], getHomeData),
            await queryClient.prefetchQuery(["metaTags", ESeoPage.HOME], () =>
                getSeoInfo(ESeoPage.HOME)
            ),
        ]);

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
            },
        };
    }
);

export default Home;
